import type { TurnWorld, Unit, UnitType, BattleScript, BattleSide } from './types';
import { UNIT_DEFS, resolveBaseType } from './units';
import { TERRAIN_DEFS } from './terrain';
import { BUILDING_DEFS } from './buildings';
import { getEffectiveAttack, consumeAttackSupply } from './supply';
import { getEffectiveness } from './combat-matrix';
import { levelForXp, xpMultiplier } from './xp';
import { activeAurasFor, auraMultiplier } from './auras';

/** Calculate raw damage from attacker to defender. */
export function calculateDamage(
  world: TurnWorld,
  attacker: Unit,
  defender: Unit,
  counterMultiplier = 1.0,
): number {
  const atkDef = UNIT_DEFS[attacker.type];
  const defDef = UNIT_DEFS[defender.type];

  const terrain = world.map.tiles[defender.row][defender.col].type;
  // Defense modifier comes from a SINGLE source: a completed building on the
  // defender's tile overrides the terrain bonus; otherwise the terrain's own
  // bonus applies. Terrain and building defense never stack.
  let defenseMultiplier = TERRAIN_DEFS[terrain].defenseBonus;
  for (const [, b] of world.buildings) {
    if (b.col === defender.col && b.row === defender.row) {
      if (b.buildProgress === 0) {
        defenseMultiplier = BUILDING_DEFS[b.type].defenseBonus ?? 1.0;
      }
      break;
    }
  }

  // Veteran bonus — +10% per level on both sides (multiplicative).
  const atkBonus = xpMultiplier(levelForXp(attacker.xp));
  const defBonus = xpMultiplier(levelForXp(defender.xp));

  // Commander auras — multiply attacker's attack and defender's defence (max
  // per effect across overlapping auras), composed multiplicatively with XP.
  const atkAuraMult = auraMultiplier(activeAurasFor(world, attacker), 'attackMultiplier');
  const defAuraMult = auraMultiplier(activeAurasFor(world, defender), 'defenseMultiplier');

  const effectiveAttack = getEffectiveAttack(attacker) * atkBonus * atkAuraMult;
  const effectiveness = getEffectiveness(attacker.type, defender.type);
  const netAttack = effectiveAttack * effectiveness - defDef.defense * defenseMultiplier * defBonus * defAuraMult;
  const damage = netAttack * (attacker.hp / atkDef.maxHp) * counterMultiplier;

  return Math.max(1, Math.round(damage));
}

/** Manhattan distance (orthogonal steps) — used for movement-style heuristics. */
export function manhattanDistance(a: { col: number; row: number }, b: { col: number; row: number }): number {
  return Math.abs(a.col - b.col) + Math.abs(a.row - b.row);
}

/** Chebyshev distance (king moves) — diagonals count as 1. This is the metric
 *  for attack range, zone of control, ambush and adjacency, so range is a
 *  square "radius" that includes diagonal tiles. */
export function chebyshevDistance(a: { col: number; row: number }, b: { col: number; row: number }): number {
  return Math.max(Math.abs(a.col - b.col), Math.abs(a.row - b.row));
}

/** An indirect unit (minAttackRange ≥ 2, e.g. artillery, katiusza) cannot move
 *  and fire on the same turn. Direct units (minAttackRange 1) can. */
export function isIndirect(unit: Unit): boolean {
  return (UNIT_DEFS[unit.type].minAttackRange ?? 1) >= 2;
}

/** Submarines are stealthy: only another submarine may attack one (covers
 *  faction variants via base type). */
export function isSubmarine(type: UnitType): boolean {
  return resolveBaseType(type) === 'ship-submarine';
}

/** Position-parameterized attack check: would `attacker` be able to hit
 *  `defender` if it were standing on (fromCol, fromRow)? Checks acted, ammo,
 *  effectiveness, and range — but NOT `hasMoved` (the caller decides which
 *  firing tiles are legal). */
export function canAttackFrom(attacker: Unit, fromCol: number, fromRow: number, defender: Unit): boolean {
  if (attacker.acted) return false;
  const atkDef = UNIT_DEFS[attacker.type];
  // Units that *use* ammo need at least one round loaded. Units with
  // maxAmmo === 0 (like supply trucks) already get filtered out by
  // attackRange === 0, but the guard is harmless.
  if (atkDef.maxAmmo > 0 && attacker.ammo <= 0) return false;
  // Submarines are stealthy (they hide underwater): ONLY another submarine may
  // attack a submarine — no other unit can target one, regardless of effectiveness.
  if (isSubmarine(defender.type) && !isSubmarine(attacker.type)) return false;
  if (getEffectiveness(attacker.type, defender.type) === 0) return false;
  const dist = Math.max(Math.abs(fromCol - defender.col), Math.abs(fromRow - defender.row));
  const minRange = atkDef.minAttackRange ?? 1;
  return dist >= minRange && dist <= atkDef.attackRange;
}

/** Check if attacker can attack defender from its current tile. Returns false when:
 *  - attacker has already acted this turn,
 *  - attacker is indirect and has moved this turn (move OR attack, not both),
 *  - effectiveness matrix says 0 (e.g. infantry vs battleship),
 *  - attacker has run out of ammo (cannot fire),
 *  - target is out of range (respects min/max attackRange). */
export function canAttack(attacker: Unit, defender: Unit): boolean {
  if (attacker.hasMoved && isIndirect(attacker)) return false;
  return canAttackFrom(attacker, attacker.col, attacker.row, defender);
}

export interface CombatResult {
  damage: number;           // damage dealt to defender
  defenderKilled: boolean;
  counterDamage: number;    // damage dealt back to attacker (0 if no counter)
  attackerKilled: boolean;
}

/**
 * Resolve combat: attacker hits defender, then defender may counter.
 * Mutates unit HP in-place. Removes killed units from world.
 */
export function resolveCombat(world: TurnWorld, attacker: Unit, defender: Unit): CombatResult {
  // Consume supply for attacking
  consumeAttackSupply(attacker);

  // Primary attack
  const damage = calculateDamage(world, attacker, defender);
  defender.hp -= damage;

  const defenderKilled = defender.hp <= 0;

  let counterDamage = 0;
  let attackerKilled = false;

  if (defenderKilled) {
    // Remove defender and reward the attacker with XP for the kill.
    world.units.delete(defender.id);
    attacker.xp += 1;
  } else {
    // Counter-attack: only if defender has range to reach attacker (respects minAttackRange)
    const defDef = UNIT_DEFS[defender.type];
    const dist = chebyshevDistance(attacker, defender);
    const defMinRange = defDef.minAttackRange ?? 1;
    if (dist >= defMinRange && dist <= defDef.attackRange) {
      counterDamage = calculateDamage(world, defender, attacker, 0.7);
      attacker.hp -= counterDamage;

      if (attacker.hp <= 0) {
        attackerKilled = true;
        world.units.delete(attacker.id);
        // Counter-kill — defender (still alive) gets XP for finishing the attacker.
        defender.xp += 1;
      }
    }
  }

  // Mark attacker as acted (if still alive)
  if (!attackerKilled) {
    attacker.acted = true;
  }

  return { damage, defenderKilled, counterDamage, attackerKilled };
}

/** Token count for the battle scene: each token = 20% of max HP. Zero when
 *  destroyed, at least one while any HP remains, capped at five. */
export function battleTokens(hp: number, maxHp: number): number {
  if (hp <= 0) return 0;
  return Math.min(5, Math.max(1, Math.ceil((hp / maxHp) * 5)));
}

/**
 * Build a deterministic battle script around an engagement, then resolve it.
 * Captures HP-before/terrain/domain for both sides, runs `resolveCombat`
 * (which mutates HP and removes killed units), and records the after state.
 * The combat outcome is exactly that of `resolveCombat` — this only observes.
 */
export function buildBattleScript(world: TurnWorld, attacker: Unit, defender: Unit): BattleScript {
  const atkDef = UNIT_DEFS[attacker.type];
  const defDef = UNIT_DEFS[defender.type];

  // Structure on a tile (drives an urban battle background): 'city' for city
  // tiles, 'building' for any other completed structure.
  const structureAt = (col: number, row: number): 'city' | 'building' | undefined => {
    for (const [, b] of world.buildings) {
      if (b.col === col && b.row === row) return b.type === 'city' ? 'city' : 'building';
    }
    return undefined;
  };

  // Capture everything that resolveCombat may mutate or that depends on position.
  const atkBefore = {
    type: attacker.type, owner: attacker.owner, domain: atkDef.domain,
    terrain: world.map.tiles[attacker.row][attacker.col].type,
    structure: structureAt(attacker.col, attacker.row),
    maxHp: atkDef.maxHp, hpBefore: attacker.hp,
  };
  const defBefore = {
    type: defender.type, owner: defender.owner, domain: defDef.domain,
    terrain: world.map.tiles[defender.row][defender.col].type,
    structure: structureAt(defender.col, defender.row),
    maxHp: defDef.maxHp, hpBefore: defender.hp,
  };

  const result = resolveCombat(world, attacker, defender);

  const atkHpAfter = Math.max(0, attacker.hp);
  const defHpAfter = Math.max(0, defender.hp);

  const attackerSide: BattleSide = {
    ...atkBefore,
    hpAfter: atkHpAfter,
    spritesBefore: battleTokens(atkBefore.hpBefore, atkBefore.maxHp),
    spritesAfter: battleTokens(atkHpAfter, atkBefore.maxHp),
    killed: result.attackerKilled,
  };
  const defenderSide: BattleSide = {
    ...defBefore,
    hpAfter: defHpAfter,
    spritesBefore: battleTokens(defBefore.hpBefore, defBefore.maxHp),
    spritesAfter: battleTokens(defHpAfter, defBefore.maxHp),
    killed: result.defenderKilled,
  };

  return {
    attacker: attackerSide,
    defender: defenderSide,
    damage: result.damage,
    counterDamage: result.counterDamage,
    counterHappened: result.counterDamage > 0,
  };
}

/**
 * Execute move-then-attack: move unit to target tile, then attack adjacent enemy.
 * Returns null if attack target is not adjacent after move.
 */
export function moveAndAttack(
  world: TurnWorld,
  unit: Unit,
  moveCol: number,
  moveRow: number,
  target: Unit,
): CombatResult | null {
  // Move unit
  if (moveCol !== unit.col || moveRow !== unit.row) {
    unit.col = moveCol;
    unit.row = moveRow;
    unit.hasMoved = true;
  }

  // Verify target is now in attack range (indirect units that moved cannot fire)
  if (!canAttack(unit, target)) {
    return null;
  }

  return resolveCombat(world, unit, target);
}

/** Fraction of normal damage each ambusher deals when a unit walks into them. */
export const AMBUSH_DAMAGE_FRACTION = 0.4;

/**
 * Apply ambush damage: every ambusher fires a free, reduced reaction shot at the
 * victim (a fraction of normal damage). Mutates the victim's HP and removes it
 * from the world if it dies. The ambushers do NOT spend ammo or their action —
 * it's a surprise reaction. Returns the total damage dealt and whether the
 * victim was killed.
 */
export function applyAmbushDamage(
  world: TurnWorld, victim: Unit, ambushers: Unit[], fraction = AMBUSH_DAMAGE_FRACTION,
): { totalDamage: number; died: boolean } {
  let total = 0;
  for (const a of ambushers) {
    if (getEffectiveness(a.type, victim.type) === 0) continue; // can't hurt this target
    total += Math.round(calculateDamage(world, a, victim) * fraction);
  }
  if (total <= 0) return { totalDamage: 0, died: false };
  victim.hp -= total;
  if (victim.hp <= 0) {
    world.units.delete(victim.id);
    if (ambushers.length > 0) ambushers[0].xp += 1; // credit the spring of the trap
    return { totalDamage: total, died: true };
  }
  return { totalDamage: total, died: false };
}
